import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, RubberStamp, Swatch, type SwatchKind } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { CommentsInline } from "@/components/CommentsInline";
import { ListingBadge } from "@/components/ListingBadge";
import { ListingStatusSheet } from "@/components/ListingStatusSheet";
import { PostCarousel } from "@/components/PostCarousel";
import { useFollow } from "@/context/FollowContext";
import { useGeolocation } from "@/hooks/useGeolocation";
import { useNearbyScans } from "@/hooks/useNearbyScans";
import { useAuth } from "@/hooks/useAuth";
import { useLoves } from "@/hooks/useLoves";
import { useCommentCounts } from "@/hooks/useComments";
import { distanceMi, formatDistance } from "@/lib/geo";
import { parseMarketplaceLinks, type MarketplaceLink } from "@/lib/marketplaces";
import { INTERESTS } from "@/lib/interests";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/lib/toast";

const NEARBY_RADIUS_MI = 50;

function formatTimeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

const ERA_SYNONYMS: Record<string, string[]> = {
  "50s": ["1950s", "fifties"],
  "60s": ["1960s", "sixties"],
  "70s": ["1970s", "seventies"],
  "80s": ["1980s", "eighties"],
  "90s": ["1990s", "nineties"],
  "y2k": ["2000s", "early 2000"],
  "2010s": ["twenty tens"],
};

interface ReactionPillProps {
  label: string;
  count: number;
  color: string;
  active?: boolean;
  onClick?: () => void;
  disabled?: boolean;
  title?: string;
}

function ReactionPill({ label, count, color, active, onClick, disabled, title }: ReactionPillProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: "5px 9px",
        border: `1px solid ${color}`,
        background: active ? color : "transparent",
        cursor: disabled ? "not-allowed" : onClick ? "pointer" : "default",
        opacity: disabled ? 0.45 : 1,
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: active ? "hsl(var(--cream))" : color,
        display: "flex",
        gap: 5,
        alignItems: "center",
        transition: "background 120ms",
      }}
    >
      <span>{label}</span>
      <span style={{ fontWeight: 700 }}>{count}</span>
    </button>
  );
}

// Small "Shop this" overlay chip — bottom-left of the post image when the
// owner has a marketplace listing URL attached. Visual only; opens the
// external link in a new tab.
function ShopThisChip({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer nofollow"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: "absolute",
        bottom: 10,
        left: 10,
        background: "rgba(26,20,16,0.82)",
        color: "hsl(var(--cream))",
        padding: "4px 8px",
        fontFamily: "'IBM Plex Mono', monospace",
        fontSize: 10,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        textDecoration: "none",
        border: "none",
      }}
    >Shop this</a>
  );
}

export default function Feed() {
  const nav = useNavigate();
  const [tab, setTab] = useState("Following");
  const { isFollowing, toggle } = useFollow();
  const { user } = useAuth();
  const geo = useGeolocation(true);
  // Nearby matches on the city the user typed into Settings → Home base.
  // No GPS — just a free-text city string compared to scans.city. The
  // device-geolocation hook is still mounted because the rest of the
  // Feed surface uses geo.coords for "12 mi" distance labels on cards,
  // but it is NOT what gates the Nearby filter.
  const [myCity, setMyCity] = useState<string | null>(null);
  const [myAvatar, setMyAvatar] = useState<import("@/components/primitives").SwatchKind>("cream");
  useEffect(() => {
    if (!user) return;
    // Read both `city` and `location` — older profiles that saved their
    // home base before the saveHomeBase fix only have `location` populated.
    // We fall back so those users aren't prompted to set their city again
    // when they already typed one in Settings.
    supabase.from("profiles").select("avatar, city, location").eq("id", user.id).maybeSingle()
      .then(({ data }) => {
        if (data?.avatar) setMyAvatar(data.avatar as import("@/components/primitives").SwatchKind);
        const resolved = (data?.city || data?.location || "").toString().trim();
        setMyCity(resolved.length > 0 ? resolved : null);
      });
  }, [user?.id]);
  const { nearbyScans, allPosted } = useNearbyScans(geo.coords, NEARBY_RADIUS_MI, myCity);
  const [followedIds, setFollowedIds] = useState<string[]>([]);
  const [followedIdsLoaded, setFollowedIdsLoaded] = useState(false);
  useEffect(() => {
    if (!user?.id) { setFollowedIds([]); setFollowedIdsLoaded(true); return; }
    supabase
      .from("follows")
      .select("followed_id")
      .eq("follower_id", user.id)
      .then(({ data }) => {
        setFollowedIds((data ?? []).map((f) => f.followed_id));
        setFollowedIdsLoaded(true);
      });
  }, [user?.id]);

  // Home location is set via Settings → Home base (typed city, no GPS).
  // We intentionally do NOT auto-sync device geolocation to the profile here
  // — that would overwrite the user's typed city with a captured fix.

  // Local override store so edits made via the sheet show up instantly without
  // refetching. Keyed by real scan id; values replace `marketplaceLinks` and
  // (when posted=false) hide the post entirely.
  const [localOverrides, setLocalOverrides] = useState<Record<string, {
    links: MarketplaceLink[];
    posted: boolean;
    soldAt?: string | null;
    soldPrice?: number | null;
    soldOn?: string | null;
  }>>({});

  const allPostedAsPosts = useMemo(
    () => allPosted
      .filter((s) => localOverrides[s.id]?.posted !== false)
      .map((s) => ({
        id: s.id,
        ownerId: s.user_id,
        user: s.display_name || `@${s.handle}`,
        handle: `@${s.handle}${s.city ? ` · ${s.city}` : ""}`,
        avatar: s.avatar,
        time: formatTimeAgo(s.created_at),
        title: s.title,
        era: s.era ?? "—",
        brand: s.brand ?? "—",
        price: s.price_suggested,
        img: (s.photos[0] || s.img || "olive") as import("@/components/primitives").SwatchKind | string,
        photos: (s.photos && s.photos.length > 0 ? s.photos : []) as string[],
        verdict: s.verdict,
        conf: s.confidence ?? 0,
        caption: s.caption ?? "Just posted.",
        reactions: { buy: 0, grail: 0 },
        comments: 0,
        coords: { lat: s.lat, lng: s.lng },
        distanceMi: s.distanceMi,
        postedTo: s.posted_to ?? null,
        marketplaceLinks: localOverrides[s.id]?.links ?? s.marketplace_links,
        soldAt: localOverrides[s.id]?.soldAt !== undefined
          ? localOverrides[s.id]?.soldAt ?? null
          : (s.sold_at || (s.status && /sold/i.test(s.status) ? s.created_at : null)),
        soldPrice: localOverrides[s.id]?.soldPrice !== undefined
          ? localOverrides[s.id]?.soldPrice ?? null
          : s.sold_price,
        soldOn: localOverrides[s.id]?.soldOn !== undefined
          ? localOverrides[s.id]?.soldOn ?? null
          : s.posted_to,
        verification: s.verification,
        category: s.category ?? null,
        tags: s.tags ?? [],
        is_verified: s.is_verified,
        // Quick-posts (post_kind === "manual") never ran through fig-verify.
        // Their verdict column is a NOT-NULL placeholder, so don't render a
        // VINTAGE / FAST FASH rubber-stamp over their photos.
        unverified: s.post_kind === "manual",
      })),
    [allPosted, localOverrides]
  );

  const realNearbyAsPosts = useMemo(
    () => nearbyScans
      // Drop posts the owner just unposted in this session.
      .filter((s) => localOverrides[s.id]?.posted !== false)
      .map((s) => ({
      id: s.id,
      ownerId: s.user_id,
      user: s.display_name || `@${s.handle}`,
      handle: `@${s.handle}${s.city ? ` · ${s.city}` : ""}`,
      avatar: s.avatar,
      time: formatTimeAgo(s.created_at),
      title: s.title,
      era: s.era ?? "—",
      brand: s.brand ?? "—",
      price: s.price_suggested,
      img: (s.photos[0] || s.img || "olive") as SwatchKind | string,
      photos: (s.photos && s.photos.length > 0 ? s.photos : []) as string[],
      verdict: s.verdict,
      conf: s.confidence ?? 0,
      caption: s.caption ?? "Just posted.",
      reactions: { buy: 0, grail: 0 },
      comments: 0,
      coords: { lat: s.lat, lng: s.lng },
      distanceMi: s.distanceMi,
      postedTo: s.posted_to ?? null,
      marketplaceLinks: localOverrides[s.id]?.links ?? s.marketplace_links,
      soldAt: localOverrides[s.id]?.soldAt !== undefined
        ? localOverrides[s.id]?.soldAt ?? null
        : (s.sold_at || (s.status && /sold/i.test(s.status) ? s.created_at : null)),
      soldPrice: localOverrides[s.id]?.soldPrice !== undefined
        ? localOverrides[s.id]?.soldPrice ?? null
        : s.sold_price,
      soldOn: localOverrides[s.id]?.soldOn !== undefined
        ? localOverrides[s.id]?.soldOn ?? null
        : s.posted_to,
      verification: s.verification,
      category: s.category ?? null,
      tags: s.tags ?? [],
      is_verified: s.is_verified,
      unverified: s.post_kind === "manual",
    })),
    [nearbyScans, localOverrides]
  );

  // Nearby is keyed on the typed Home Base city (metro-normalized) — GPS is
  // only used to compute "12 mi" distance labels and to sort by proximity
  // within the matched metro. If the user denied geolocation but has a city
  // set, useNearbyScans still returns metro matches; we just can't sort by
  // distance, so Infinity sentinels fall through to insertion order.
  const nearbyPosts = useMemo(
    () => realNearbyAsPosts.slice().sort(
      (a, b) => (a.distanceMi ?? Infinity) - (b.distanceMi ?? Infinity)
    ),
    [realNearbyAsPosts]
  );

  const followingPosts = useMemo(
    () => allPostedAsPosts.filter((p) => followedIds.includes(p.ownerId)),
    [allPostedAsPosts, followedIds]
  );

  const visiblePosts =
    tab === "Following" ? followingPosts
    : tab === "Nearby" ? nearbyPosts
    : allPostedAsPosts;

  // ── Search & interest filters ──────────────────────────────────────────
  // Free-text search runs across title/brand/era/caption/handle. Interest
  // chips are drawn from the same shared catalog used in onboarding so the
  // labels match what the collector has already self-tagged with.
  const [search, setSearch] = useState("");
  const [profileResults, setProfileResults] = useState<{ id: string; handle: string; display_name: string; avatar: string | null }[]>([]);
  const [profileSearching, setProfileSearching] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    const q = search.trim();
    if (q.length < 2) { setProfileResults([]); return; }
    setProfileSearching(true);
    searchDebounce.current = setTimeout(async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, handle, display_name, avatar")
        .or(`handle.ilike.%${q}%,display_name.ilike.%${q}%`)
        .neq("id", user?.id ?? "")
        .limit(8);
      setProfileResults((data ?? []) as { id: string; handle: string; display_name: string; avatar: string | null }[]);
      setProfileSearching(false);
    }, 300);
    return () => { if (searchDebounce.current) clearTimeout(searchDebounce.current); };
  }, [search, user]);

  const [activeInterests, setActiveInterests] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  // Personalised mode: when on, the user's saved profile.tags act as soft
  // default filters layered on top of any chips they've explicitly toggled.
  // Stored separately from `activeInterests` so toggling Personalised off
  // doesn't wipe out filters they manually picked.
  const [profileTags, setProfileTags] = useState<string[]>([]);
  // Default OFF so the base feed shows every item; the "My interests"
  // button on the filter strip toggles personalised filtering on.
  const [personalised, setPersonalised] = useState(false);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [listedOnly, setListedOnly] = useState(false);
  // Pull the signed-in user's saved interests once. Anonymous users get an
  // empty array, which means the Personalised pill silently has no effect.
  useEffect(() => {
    if (!user) { setProfileTags([]); return; }
    let cancelled = false;
    supabase.from("profiles").select("tags").eq("id", user.id).maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        setProfileTags(Array.isArray(data?.tags) ? (data!.tags as string[]) : []);
      });
    return () => { cancelled = true; };
  }, [user]);

  const toggleInterest = (value: string) => {
    setActiveInterests((cur) =>
      cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value]
    );
  };
  const clearFilters = () => {
    setSearch("");
    setActiveInterests([]);
    setVerifiedOnly(false);
    setListedOnly(false);
  };

  // Effective interest filter set = manual chips + (personalised ? profileTags : []).
  // De-duped so the same tag isn't checked twice.
  const effectiveInterests = useMemo(() => {
    const set = new Set(activeInterests);
    if (personalised) profileTags.forEach((t) => set.add(t));
    return Array.from(set);
  }, [activeInterests, personalised, profileTags]);

  const filteredPosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q && effectiveInterests.length === 0 && !verifiedOnly && !listedOnly) return visiblePosts;
    return visiblePosts.filter((p) => {
      if (verifiedOnly && !p.is_verified) return false;
      if (listedOnly) {
        const links = p.marketplaceLinks;
        const hasLink = Array.isArray(links) ? links.some((l: any) => l.url) : (links && typeof links === "object" && Object.keys(links).length > 0);
        if (!hasLink) return false;
      }
      if (q) {
        const hay = [p.title, p.brand, p.era, p.caption, p.user, p.handle]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (effectiveInterests.length > 0) {
        const hay = [p.title, p.brand, p.era, p.caption, p.category, ...(p.tags || [])]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        const manualOk = activeInterests.every((interest) => {
          const meta = INTERESTS.find((i) => i.value === interest);
          const synonyms = ERA_SYNONYMS[interest] ?? [];
          const needles = [interest, meta?.label, ...synonyms]
            .filter(Boolean)
            .map((s) => (s as string).toLowerCase());
          return needles.some((n) => hay.includes(n));
        });
        if (!manualOk) return false;
        if (activeInterests.length === 0 && personalised && profileTags.length > 0) {
          const personalOk = profileTags.some((interest) => {
            const meta = INTERESTS.find((i) => i.value === interest);
            const synonyms = ERA_SYNONYMS[interest] ?? [];
            const needles = [interest, meta?.label, ...synonyms]
              .filter(Boolean)
              .map((s) => (s as string).toLowerCase());
            return needles.some((n) => hay.includes(n));
          });
          if (!personalOk) return false;
        }
      }
      return true;
    });
  }, [visiblePosts, search, activeInterests, effectiveInterests, personalised, profileTags, verifiedOnly, listedOnly]);

  const loves = useLoves(filteredPosts.map((p) => p.id));
  const commentCounts = useCommentCounts(filteredPosts.map((p) => p.id));

  // Active comments sheet target.
  const [expandedComments, setExpandedComments] = useState<Set<string>>(new Set());
  const toggleComments = (id: string) =>
    setExpandedComments((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  // Toggle comments AND scroll the section into view when it opens.
  // CommentsInline already auto-focuses its textarea (with preventScroll);
  // this adds the page-scroll piece. We look up the wrapper via a data
  // attribute since the feed renders many comment sections dynamically.
  const handleNotesClick = (id: string) => {
    const willOpen = !expandedComments.has(id);
    toggleComments(id);
    if (willOpen) {
      window.setTimeout(() => {
        const el = document.querySelector<HTMLElement>(`[data-comments-id="${id}"]`);
        el?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 60);
    }
  };
  // Active listing-status sheet target (own posts only).
  const [openListing, setOpenListing] = useState<{
    id: string;
    title: string;
    links: MarketplaceLink[];
    soldAt: string | null;
    soldPrice: number | null;
    soldOn: string | null;
  } | null>(null);

  // Native share with copy-link fallback. Works on real posts and demo posts
  // (we just synthesize a URL pointing at the feed).
  const sharePost = async (postId: string, title: string, handle: string) => {
    const url = `${window.location.origin}/feed?scan=${encodeURIComponent(postId)}`;
    const shareData = {
      title: `${title} — via Fig`,
      text: `${title} (filed by ${handle}) — found on Fig`,
      url,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch (e) {
      // User dismissed share sheet — that's not an error worth surfacing.
      if ((e as DOMException)?.name === "AbortError") return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const goProfile = (handle: string) => {
    if (handle === "@you") nav("/you");
    else nav(`/profile/${encodeURIComponent(handle)}`);
  };

  // One-tap "mark sold" for owners — flips the scan to sold without opening
  // the listing sheet. Used when the collector sold the piece on the marketplace
  // already linked, so we don't need to ask them where or for how much.
  const markSoldQuick = async (scanId: string, title: string, soldOn: string | null) => {
    if (!user) return;
    if (!confirm(`Mark "${title}" as sold${soldOn ? ` on ${soldOn}` : ""}?`)) return;
    const nowIso = new Date().toISOString();
    const update = {
      status: "sold",
      sold_at: nowIso,
      posted_to: soldOn ? soldOn.toLowerCase() : null,
    };
    const { error } = await supabase
      .from("scans")
      .update(update as unknown as Record<string, never>)
      .eq("id", scanId);
    if (error) { toast.error("Couldn't mark sold"); return; }
    toast.success("Marked as sold 🎉");
    setLocalOverrides((cur) => ({
      ...cur,
      [scanId]: {
        links: cur[scanId]?.links ?? [],
        posted: cur[scanId]?.posted ?? true,
        soldAt: nowIso,
        soldPrice: cur[scanId]?.soldPrice ?? null,
        soldOn: soldOn ?? null,
      },
    }));
  };

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 40 }}>
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav("/home")} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>{Icon.chev(18, "l")}</button>
          <span className="caps">Daily Edition</span>
          <button onClick={() => nav("/you")} style={{ background: "none", border: "none", padding: 6, cursor: "pointer" }}>
            <Swatch kind={myAvatar} style={{ width: 26, height: 26, border: "1px solid hsl(var(--ink))", borderRadius: "50%" }} />
          </button>
        </div>

        <div style={{ padding: "10px 20px 0" }}>
          <span className="caps" style={{ color: "hsl(var(--rust))" }}>Apr 18 · 7:42 AM</span>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 48, color: "hsl(var(--ink))", letterSpacing: "-0.03em", lineHeight: 0.9, marginTop: 4 }}>
            The Rack
          </div>
          <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 4 }}>
            what your people found today
          </div>
        </div>

        {/* Scope pills — Following / Nearby / Global. These replace the old
            Grails/Leaders/Closet tile row at the top of the feed; the search
            field sits directly below for a clean discovery flow. */}
        <div style={{ padding: "16px 20px 0", display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {(["Following", "Nearby", "Global"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: "6px 14px", border: "1px solid hsl(var(--ink))",
              borderRadius: 999, cursor: "pointer",
              background: tab === t ? "hsl(var(--ink))" : "transparent",
              color: tab === t ? "hsl(var(--cream))" : "hsl(var(--ink))",
              fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
              display: "flex", alignItems: "center", gap: 4,
            }}>
              {t}
              {t === "Nearby" && myCity && <span style={{ opacity: 0.7 }}>· {myCity}</span>}
            </button>
          ))}
        </div>

        {/* Search + interest filters. Search field is always visible so the
            collector can hunt by keyword from any tab; the filter drawer below
            it is collapsed by default to keep the page calm. */}
        <div style={{ padding: "12px 20px 0", display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "stretch" }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, brand, era, collector…"
              style={{
                flex: 1,
                padding: "8px 10px",
                border: "1px solid hsl(var(--ink))",
                background: "hsl(var(--paper))",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 12,
                color: "hsl(var(--ink))",
                outline: "none",
              }}
            />
            <button
              onClick={() => setShowFilters((s) => !s)}
              style={{
                padding: "5px 10px",
                border: "1px solid hsl(var(--ink))",
                cursor: "pointer",
                background: showFilters || activeInterests.length > 0 || verifiedOnly ? "hsl(var(--ink))" : "transparent",
                color: showFilters || activeInterests.length > 0 || verifiedOnly ? "hsl(var(--cream))" : "hsl(var(--ink))",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              Filters{(activeInterests.length > 0 || verifiedOnly) ? ` · ${activeInterests.length + (verifiedOnly ? 1 : 0)}` : ""}
            </button>
          </div>

          {showFilters && (
            <div
              style={{
                border: "1px solid hsl(var(--ink))",
                background: "hsl(var(--cream))",
                borderRadius: 18,
                padding: "10px 12px",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {profileTags.length > 0 && (
                <div>
                  <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
                    Personalise
                  </div>
                  <button
                    onClick={() => setPersonalised((p) => !p)}
                    title={personalised
                      ? `Filtering by your ${profileTags.length} saved interests`
                      : "Show all posts"}
                    style={{
                      padding: "5px 12px",
                      border: "1px solid hsl(var(--rust))",
                      borderRadius: 999,
                      cursor: "pointer",
                      background: personalised ? "hsl(var(--rust))" : "transparent",
                      color: personalised ? "hsl(var(--cream))" : "hsl(var(--rust))",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: 5,
                    }}
                  >
                    <span>{Icon.sparkle(10)}</span>
                    My interests
                  </button>
                </div>
              )}
              <div>
                <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
                  Quality
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  <button
                    onClick={() => setVerifiedOnly((v) => !v)}
                    style={{
                      padding: "5px 12px",
                      border: "1px solid hsl(var(--ink))",
                      borderRadius: 999,
                      cursor: "pointer",
                      background: verifiedOnly ? "hsl(var(--ink))" : "transparent",
                      color: verifiedOnly ? "hsl(var(--cream))" : "hsl(var(--ink))",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: 5,
                    }}
                  >
                    Verified only
                  </button>
                  <button
                    onClick={() => setListedOnly((v) => !v)}
                    style={{
                      padding: "5px 12px",
                      border: "1px solid hsl(var(--ink))",
                      borderRadius: 999,
                      cursor: "pointer",
                      background: listedOnly ? "hsl(var(--ink))" : "transparent",
                      color: listedOnly ? "hsl(var(--cream))" : "hsl(var(--ink))",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                      display: "inline-flex", alignItems: "center", gap: 5,
                    }}
                  >
                    Listed only
                  </button>
                </div>
              </div>
              {(["Categories", "Eras", "Aesthetics", "Regions"] as const).map((group) => (
                <div key={group}>
                  <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
                    {group}
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                    {INTERESTS.filter((i) => i.group === group).map((i) => {
                      const active = activeInterests.includes(i.value);
                      return (
                        <button
                          key={i.value}
                          onClick={() => toggleInterest(i.value)}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid hsl(var(--ink))",
                            cursor: "pointer",
                            background: active ? "hsl(var(--ink))" : "transparent",
                            color: active ? "hsl(var(--cream))" : "hsl(var(--ink))",
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: 10,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                          }}
                        >
                          {i.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {(activeInterests.length > 0 || search) && (
                <button
                  onClick={clearFilters}
                  style={{
                    alignSelf: "flex-start",
                    padding: "4px 8px",
                    border: "1px dashed hsl(var(--ink-2))",
                    background: "transparent",
                    cursor: "pointer",
                    color: "hsl(var(--ink-2))",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  Clear all
                </button>
              )}
            </div>
          )}

          {/* Compact summary row when filters are collapsed but active. */}
          {!showFilters && (activeInterests.length > 0 || verifiedOnly) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
              {verifiedOnly && (
                <button
                  onClick={() => setVerifiedOnly(false)}
                  title="Remove filter"
                  style={{
                    padding: "3px 7px",
                    border: "1px solid hsl(var(--ink))",
                    background: "hsl(var(--ink))",
                    color: "hsl(var(--cream))",
                    cursor: "pointer",
                    fontFamily: "'IBM Plex Mono', monospace",
                    fontSize: 10,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  Verified only ×
                </button>
              )}
              {activeInterests.map((value) => {
                const meta = INTERESTS.find((i) => i.value === value);
                return (
                  <button
                    key={value}
                    onClick={() => toggleInterest(value)}
                    title="Remove filter"
                    style={{
                      padding: "3px 7px",
                      border: "1px solid hsl(var(--ink))",
                      background: "hsl(var(--ink))",
                      color: "hsl(var(--cream))",
                      cursor: "pointer",
                      fontFamily: "'IBM Plex Mono', monospace",
                      fontSize: 10,
                      letterSpacing: "0.08em",
                      textTransform: "uppercase",
                    }}
                  >
                    {meta?.label ?? value} ×
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {tab === "Nearby" && !myCity && (
          <div style={{ padding: "12px 20px 0" }}>
            <div style={{ border: "1px dashed hsl(var(--ink))", background: "hsl(var(--cream))", padding: "14px", display: "flex", gap: 12, alignItems: "center" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15, color: "hsl(var(--ink))" }}>
                  Set your home base
                </div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12, color: "hsl(var(--ink-2))", marginTop: 2 }}>
                  Type your city in Settings to see finds from other collectors in the same city.
                </div>
              </div>
              <button onClick={() => nav("/settings?tab=account")} style={{
                background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))", border: "none",
                padding: "8px 12px", cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
              }}>Set city</button>
            </div>
          </div>
        )}

        {tab === "Nearby" && myCity && nearbyPosts.length === 0 && (
          <div style={{ padding: "24px 20px 0", textAlign: "center" }}>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))" }}>
              No finds from {myCity} yet — be the first to post.
            </div>
          </div>
        )}

        {tab === "Following" && followedIdsLoaded && followedIds.length === 0 && (
          <div style={{ padding: "24px 20px 0", textAlign: "center" }}>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))", lineHeight: 1.45 }}>
              you are not following anyone yet. find collectors whose taste you trust and follow them from their profile or the rack.
            </div>
          </div>
        )}
        {tab === "Following" && followedIdsLoaded && followedIds.length > 0 && followingPosts.length === 0 && (
          <div style={{ padding: "24px 20px 0", textAlign: "center" }}>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))", lineHeight: 1.45 }}>
              none of the people you follow have posted yet. check back soon.
            </div>
          </div>
        )}

        {/* ── Collector search results ─────────────────────────────────────── */}
        {search.trim().length >= 2 && (profileResults.length > 0 || profileSearching) && (
          <div style={{ padding: "14px 20px 0" }}>
            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 8 }}>
              Collectors{profileSearching ? " ·" : profileResults.length > 0 ? ` · ${profileResults.length}` : ""}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {profileResults.map((p) => {
                const following = isFollowing(p.id);
                return (
                  <div key={p.id} style={{ background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px" }}>
                    <button
                      onClick={() => goProfile(p.handle || p.id)}
                      style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, flex: 1 }}
                    >
                      <Swatch kind={(p.avatar ?? "cream") as SwatchKind} style={{ width: 36, height: 36, borderRadius: "50%", border: "1px solid hsl(var(--ink))", flexShrink: 0 }} />
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))", lineHeight: 1.1 }}>
                          {p.display_name || p.handle}
                        </div>
                        {p.handle && (
                          <div className="caps" style={{ marginTop: 3, color: "hsl(var(--ink-2))" }}>@{p.handle}</div>
                        )}
                      </div>
                    </button>
                    <button onClick={() => toggle(p.id)} style={{
                      background: following ? "hsl(var(--ink))" : "transparent",
                      border: "1px solid hsl(var(--ink))", padding: "4px 8px",
                      cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: "0.1em",
                      textTransform: "uppercase", color: following ? "hsl(var(--cream))" : "hsl(var(--ink))",
                      flexShrink: 0,
                    }}>{following ? "Following" : "Follow"}</button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ padding: "14px 20px 0", display: "flex", flexDirection: "column", gap: 18 }}>
          {filteredPosts.length === 0 && (search || activeInterests.length > 0 || listedOnly) && (
            <div style={{ background: "hsl(var(--cream))", border: "1px dashed hsl(var(--ink))", padding: "20px 14px", textAlign: "center" }}>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 15, color: "hsl(var(--ink-2))" }}>
                {listedOnly && !search && activeInterests.length === 0
                  ? "No listed items right now. Check back soon — new finds get posted daily."
                  : search.trim().length >= 2 && profileResults.length === 0 && !profileSearching
                  ? `No results for "${search.trim()}"\nTry a brand name, era, or username.`
                  : "Nothing matches your hunt."}
              </div>
              <button
                onClick={clearFilters}
                style={{
                  marginTop: 10,
                  padding: "6px 10px",
                  border: "1px solid hsl(var(--ink))",
                  background: "transparent",
                  cursor: "pointer",
                  color: "hsl(var(--ink))",
                  fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 10,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                }}
              >
                Clear search & filters
              </button>
            </div>
          )}
          {filteredPosts.map((p) => {
            const following = isFollowing(p.ownerId);
            return (
              <div key={p.id} style={{ position: "relative" }}>
                <div style={{ background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", borderBottom: "1px dashed hsl(var(--sand-deep))" }}>
                    <button onClick={() => goProfile(p.user)} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", display: "flex", alignItems: "center", gap: 10, flex: 1 }}>
                      <Swatch kind={p.avatar} style={{ width: 32, height: 32, borderRadius: "50%", border: "1px solid hsl(var(--ink))" }} />
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))", lineHeight: 1.1 }}>
                          {p.user}{p.handle ? <> · {p.handle}</> : null}
                        </div>
                        <div className="caps mx-0 rounded-sm" style={{ marginTop: 4 }}>
                          {p.time}
                          {p.distanceMi != null && (
                            <> · <span style={{ color: "hsl(var(--rust))" }}>{formatDistance(p.distanceMi)}</span></>
                          )}
                        </div>
                      </div>
                    </button>
                    <button onClick={() => toggle(p.ownerId)} style={{
                      background: following ? "hsl(var(--ink))" : "transparent",
                      border: "1px solid hsl(var(--ink))", padding: "4px 8px",
                      cursor: "pointer", fontFamily: "'IBM Plex Mono', monospace", fontSize: 9, letterSpacing: "0.1em",
                      textTransform: "uppercase", color: following ? "hsl(var(--cream))" : "hsl(var(--ink))",
                    }}>{following ? "Following" : "Follow"}</button>
                  </div>

                  {(() => {
                    const urlPhotos = (p.photos ?? []).filter(
                      (s): s is string => typeof s === "string" && /^https?:\/\//.test(s)
                    );
                    const isReal = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(p.id);
                    const openPost = () => isReal ? nav(`/post/${p.id}`) : nav("/result");
                    if (urlPhotos.length > 1) {
                      return (
                        <PostCarousel
                          photos={urlPhotos}
                          alt={p.title}
                          verdict={p.verdict}
                          conf={p.conf}
                          soldAt={p.soldAt}
                          soldPrice={p.soldPrice}
                          soldOn={p.soldOn}
                          onClick={openPost}
                          unverified={p.unverified}
                        />
                      );
                    }
                    return (
                  <button onClick={openPost} style={{ padding: 0, border: "none", background: "none", cursor: "pointer", display: "block", width: "100%" }}>
                    {typeof p.img === "string" && /^https?:\/\//.test(p.img) ? (
                      <div style={{ position: "relative", width: "100%", borderBottom: "1px solid hsl(var(--ink))", background: "hsl(var(--paper-3))" }}>
                        <img src={p.img} alt={p.title} style={{ width: "100%", height: "auto", display: "block" }} />
                        {!p.unverified && (
                          <div style={{ position: "absolute", top: 10, right: 10 }}>
                            <RubberStamp
                              label={p.verdict === "PASS" ? "VINTAGE" : "FAST FASH"}
                              sub={p.verdict === "PASS" ? "" : "REJECTED"}
                              color={p.verdict === "PASS" ? "hsl(var(--rust))" : "hsl(var(--ink))"}
                              size={70}
                            />
                          </div>
                        )}
                        {p.marketplaceLinks[0]?.url && (
                          <ShopThisChip url={p.marketplaceLinks[0].url} />
                        )}
                        {p.soldAt && (
                          <div style={{
                            position: "absolute", inset: 0,
                            background: "hsla(0,0%,0%,0.35)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            pointerEvents: "none",
                          }}>
                            <RubberStamp
                              label="SOLD"
                              sub={p.soldPrice != null ? `$${Number(p.soldPrice).toFixed(0)}` : (p.soldOn ?? "")}
                              color="hsl(var(--rust))"
                              size={130}
                            />
                          </div>
                        )}
                      </div>
                    ) : (
                      <Swatch kind={p.img as SwatchKind} style={{ width: "100%", aspectRatio: "4/3", position: "relative", borderBottom: "1px solid hsl(var(--ink))" }}>
                        {!p.unverified && (
                          <div style={{ position: "absolute", top: 10, right: 10 }}>
                            <RubberStamp
                              label={p.verdict === "PASS" ? "VINTAGE" : "FAST FASH"}
                              sub={p.verdict === "PASS" ? "" : "REJECTED"}
                              color={p.verdict === "PASS" ? "hsl(var(--rust))" : "hsl(var(--ink))"}
                              size={70}
                            />
                          </div>
                        )}
                        {p.marketplaceLinks[0]?.url && (
                          <ShopThisChip url={p.marketplaceLinks[0].url} />
                        )}
                        {p.soldAt && (
                          <div style={{
                            position: "absolute", inset: 0,
                            background: "hsla(0,0%,0%,0.35)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            pointerEvents: "none",
                          }}>
                            <RubberStamp
                              label="SOLD"
                              sub={p.soldPrice != null ? `$${Number(p.soldPrice).toFixed(0)}` : (p.soldOn ?? "")}
                              color="hsl(var(--rust))"
                              size={130}
                            />
                          </div>
                        )}
                      </Swatch>
                    )}
                  </button>
                    );
                  })()}

                  <div style={{ padding: "10px 12px 12px" }}>
                    <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 17, color: "hsl(var(--ink))", lineHeight: 1.15 }}>
                      {p.title}
                    </div>
                    <div className="caps" style={{ marginTop: 3, color: "hsl(var(--rust))" }}>{p.era} · {p.brand}</div>
                    {(() => {
                      const isOwner = !!user && p.ownerId === user.id;
                      // Only render the badge when there's something to say:
                      // either the collector has marketplace links, or it's
                      // their own post (so they can manage it / mark Kept).
                      if (p.marketplaceLinks.length === 0 && !isOwner) return null;
                      // Stacked below the title (instead of beside it) so long
                      // marketplace names don't squeeze the title into a narrow
                      // column on narrow screens.
                      return (
                        <div style={{ marginTop: 8 }}>
                          <ListingBadge
                            links={p.marketplaceLinks}
                            isOwner={isOwner}
                            sold={!!p.soldAt}
                            onEdit={() => setOpenListing({
                              id: p.id, title: p.title, links: p.marketplaceLinks,
                              soldAt: p.soldAt, soldPrice: p.soldPrice, soldOn: p.soldOn,
                            })}
                          />
                        </div>
                      );
                    })()}
                    <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 14, color: "hsl(var(--ink-2))", marginTop: 8, lineHeight: 1.35 }}>
                      "{p.caption}"
                    </div>

                    {(() => {
                      const isOwnPost = !!user && p.ownerId === user.id;
                      const isSold = !!p.soldAt;
                      const wantCount = loves.countFor(p.id);
                      const iWant = loves.mineFor(p.id);
                      const commentCount = commentCounts.countFor(p.id);
                      return (
                        <>
                          {/* Owner-only quick "Mark sold" button when this is a
                             live listing (has marketplace links, not yet sold).
                             For collectors who've sold the item off-platform — one
                             tap moves it into Sold without opening the sheet. */}
                          {isOwnPost && !isSold && p.marketplaceLinks.length > 0 && (
                            <button
                              onClick={() => markSoldQuick(p.id, p.title, p.marketplaceLinks[0]?.name ?? null)}
                              style={{
                                marginTop: 10, width: "100%",
                                background: "hsl(var(--marigold))",
                                color: "hsl(var(--marigold-fg))",
                                border: "1.5px solid hsl(var(--ink))",
                                padding: "8px 10px", cursor: "pointer",
                                fontFamily: "'IBM Plex Mono', monospace", fontSize: 10,
                                letterSpacing: "0.12em", textTransform: "uppercase",
                                fontWeight: 600,
                              }}
                            >Mark sold on {p.marketplaceLinks[0]?.name ?? "another platform"}</button>
                          )}
                          <div style={{ display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" }}>
                            <ReactionPill
                              label="love"
                              count={wantCount}
                              color="hsl(var(--rust))"
                              active={iWant}
                              disabled={isOwnPost || isSold}
                              title={isSold ? "This piece has sold" : isOwnPost ? "You can't react to your own scan" : undefined}
                              onClick={() => loves.toggle(p.id, p.ownerId)}
                            />
                            <div style={{ flex: 1 }} />
                            {/* stopPropagation prevents CommentsInline's document
                                click listener from seeing the same click that
                                toggles the panel, which would cause a double-toggle
                                and leave the panel stuck open. */}
                            <span style={{ display: "inline-flex" }} onClick={(e) => e.stopPropagation()}>
                              <ReactionPill
                                label="notes"
                                count={commentCount}
                                color="hsl(var(--ink-2))"
                                active={expandedComments.has(p.id)}
                                onClick={() => handleNotesClick(p.id)}
                              />
                            </span>
                            <button
                              onClick={() => sharePost(p.id, p.title, p.user)}
                              aria-label="Share post"
                              style={{
                                padding: "5px 9px",
                                border: "1px solid hsl(var(--ink-2))",
                                background: "transparent",
                                cursor: "pointer",
                                color: "hsl(var(--ink-2))",
                                display: "flex", alignItems: "center", gap: 4,
                                fontFamily: "'IBM Plex Mono', monospace",
                                fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase",
                              }}
                            >
                              {Icon.share(12)}
                              <span>Share</span>
                            </button>
                          </div>
                          {isOwnPost && !p.verification && (
                            <button
                              onClick={() => nav("/result")}
                              style={{
                                marginTop: 8,
                                background: "none",
                                border: "none",
                                padding: 0,
                                cursor: "pointer",
                                color: "hsl(var(--ink-2))",
                                fontSize: 11,
                                textDecoration: "underline",
                                fontFamily: "'IBM Plex Mono', monospace",
                                letterSpacing: "0.04em",
                              }}
                            >
                              Verify this post
                            </button>
                          )}
                          {/* Wrapper carries data-comments-id so handleNotesClick
                              can scroll this specific post's comments into view. */}
                          <div data-comments-id={p.id}>
                            <CommentsInline
                              scanId={p.id}
                              open={expandedComments.has(p.id)}
                              onDismiss={() => toggleComments(p.id)}
                              onAdded={(id) => { commentCounts.bump(id, 1); void commentCounts.refreshSingle(id); }}
                              onRemoved={(id) => { commentCounts.bump(id, -1); void commentCounts.refreshSingle(id); }}
                            />
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <ListingStatusSheet
        open={openListing !== null}
        scanId={openListing?.id ?? null}
        scanTitle={openListing?.title}
        initialLinks={openListing?.links ?? []}
        initialSold={openListing ? {
          soldAt: openListing.soldAt,
          soldPrice: openListing.soldPrice,
          soldOn: openListing.soldOn,
        } : undefined}
        onClose={() => setOpenListing(null)}
        onSaved={({ links, posted, sold, deleted }) => {
          if (!openListing) return;
          if (deleted) {
            setLocalOverrides((cur) => ({
              ...cur,
              [openListing.id]: { links: [], posted: false },
            }));
            return;
          }
          setLocalOverrides((cur) => ({
            ...cur,
            [openListing.id]: {
              links, posted,
              soldAt: sold ? sold.soldAt : cur[openListing.id]?.soldAt,
              soldPrice: sold ? sold.soldPrice : cur[openListing.id]?.soldPrice,
              soldOn: sold ? sold.soldOn : cur[openListing.id]?.soldOn,
            },
          }));
        }}
      />
    </MobileFrame>
  );
}
